import axios from 'axios'
import { getAuthHeaders, handleApiError, getErrorMessage, ApiResponse } from './apiConfig'

// Utiliser l'instance axios globale configurée dans main.ts
const http = axios

function buildHeaders(contentType?: string) {
  const headers = getAuthHeaders()
  if (contentType) {
    headers['Content-Type'] = contentType
  } else {
    delete headers['Content-Type']
  }
  return headers
}

class OnboardingService {
  async getFormDatas() {
    try {
      const response = await http.get('/backoffice/onboarding/get_form_datas', {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      // Le backoffice retourne directement error.message dans le catch
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async calculateQuotiteCessible(payload: { salaire: string; quotite_cessible_pourcent: string }) {
    try {
      const response = await http.post('/backoffice/onboarding/calculate_quotite_cessible', payload, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async createAccount(formData: FormData): Promise<ApiResponse> {
    try {
      const response = await http.post('/backoffice/onboarding/create_account', formData, {
        headers: buildHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async downloadCsvTemplate(): Promise<Blob> {
    try {
      const response = await http.get('/backoffice/onboarding/download_excel_template', {
        headers: getAuthHeaders(),
        responseType: 'blob',
        validateStatus: () => true // Accepter tous les statuts pour gérer manuellement
      })
      
      // Vérifier si la réponse est une erreur HTTP
      if (response.status >= 400) {
        // Le blob peut contenir un JSON d'erreur
        try {
          const text = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.onerror = () => reject(new Error('Impossible de lire le blob'))
            reader.readAsText(response.data)
          })
          
          try {
            const errorData = JSON.parse(text)
            throw new Error(errorData.message || `Erreur HTTP ${response.status}`)
          } catch (jsonError) {
            // Ce n'est pas du JSON, c'est peut-être du texte d'erreur
            throw new Error(text || `Erreur HTTP ${response.status}`)
          }
        } catch (readError: any) {
          throw new Error(readError.message || `Erreur HTTP ${response.status}: ${response.statusText || 'Erreur lors du téléchargement'}`)
        }
      }
      
      // Vérifier le type de contenu pour s'assurer que c'est bien un CSV
      const contentType = response.headers['content-type'] || ''
      if (contentType.includes('application/json')) {
        // Le serveur a retourné du JSON au lieu d'un CSV (probablement une erreur)
        try {
          const text = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.onerror = () => reject(new Error('Impossible de lire le blob'))
            reader.readAsText(response.data)
          })
          const errorData = JSON.parse(text)
          throw new Error(errorData.message || 'Erreur lors du téléchargement du modèle')
        } catch (parseError: any) {
          throw new Error(parseError.message || 'Erreur lors du téléchargement du modèle')
        }
      }
      
      return response.data
    } catch (error: any) {
      // Si l'erreur est déjà un Error avec un message, le relancer
      if (error instanceof Error) {
        throw error
      }
      // Sinon, extraire le message de l'erreur formatée
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async prepareImportFile(file: File) {
    const formData = new FormData()
    formData.append('import_file', file)

    try {
      const response = await http.post('/backoffice/onboarding/prepare_import_file', formData, {
        headers: buildHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async importSelection(importDatas: Array<Record<string, unknown>>, collectiviteId: number, sendKycEmail = true) {
    try {
      const response = await http.post('/backoffice/onboarding/import_file_datas', { 
        import_datas: importDatas,
        send_kyc_email: sendKycEmail,
        collectivite_id: collectiviteId
      }, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }
}

export default new OnboardingService()

